import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import Modal from "../../Modal";
import BeatLoader from "react-spinners/BeatLoader";
import { toast } from "react-toastify";
import {
  getMerchantIpWhitelist,
  setMerchantIpWhitelist,
} from "../../../services/users";

const MAX_WHITELIST_IPS = 50;
const IPV4_RE =
  /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

const parseAndValidateIps = (text) => {
  const errors = [];
  const seen = new Set();
  const ips = [];

  const raw = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (raw.length > MAX_WHITELIST_IPS) {
    return {
      ips: [],
      errors: [`A maximum of ${MAX_WHITELIST_IPS} IPs is allowed`],
    };
  }

  for (const ip of raw) {
    if (ip !== "*" && !IPV4_RE.test(ip)) {
      errors.push(`Invalid IP: ${ip}`);
      continue;
    }
    if (!seen.has(ip)) {
      seen.add(ip);
      ips.push(ip);
    }
  }

  return { ips, errors };
};

const IpWhitelistModal = forwardRef(({ authToken }, ref) => {
  const modalRef = useRef(null);
  const authTokenRef = useRef(authToken);
  authTokenRef.current = authToken;
  const [userId, setUserId] = useState(null);
  const [ipsText, setIpsText] = useState("");
  const [usesEnvFallback, setUsesEnvFallback] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadIps = (uid) => {
    const token = authTokenRef.current;
    if (!uid || !token) return;
    setLoading(true);
    setError("");
    getMerchantIpWhitelist(uid, token)
      .then((res) => {
        if (res.status_code == 200) {
          const ips = res.data?.ips || [];
          setIpsText(ips.join("\n"));
          setUsesEnvFallback(Boolean(res.data?.uses_env_fallback));
        } else {
          setIpsText("");
          setUsesEnvFallback(false);
          setError(res.message || "Could not load IP whitelist");
          toast.error(`${res.message}`);
        }
      })
      .catch((err) => {
        setError("Could not load IP whitelist");
        toast.error(`${err}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useImperativeHandle(ref, () => ({
    open: (uid) => {
      setUserId(uid);
      setIpsText("");
      setUsesEnvFallback(false);
      setError("");
      modalRef.current.open();
      loadIps(uid);
    },
  }));

  const handleSave = () => {
    if (loading || saving || !userId) return;

    const { ips, errors } = parseAndValidateIps(ipsText);
    if (errors.length > 0) {
      setError(errors.join(" "));
      return;
    }

    setSaving(true);
    setError("");
    setMerchantIpWhitelist(userId, ips, authTokenRef.current)
      .then((res) => {
        if (res.status_code == 200) {
          toast.success(`${res.message}`);
          modalRef.current.close();
        } else {
          setError(res.message || "Could not update IP whitelist");
          toast.error(`${res.message}`);
        }
      })
      .catch((err) => {
        setError("Could not update IP whitelist");
        toast.error(`${err}`);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Modal
      title="Allowed IPs"
      onSubmit={handleSave}
      onClose={() => {}}
      ref={modalRef}
      submitBtn="Save"
      cancelBtn="Close"
    >
      <p className="text-sm text-[#767676] mb-3">
        One IPv4 address per line. Use * to allow all IPs. Leave empty to fall
        back to the global whitelist.
      </p>
      {usesEnvFallback && (
        <p className="text-sm text-[#0F5D5D] mb-3">
          No merchant-specific list is set. Requests currently use the global
          fallback.
        </p>
      )}
      {error && <p className="text-sm text-[#FF0F00] mb-3">{error}</p>}
      <textarea
        aria-label="Allowed IPs"
        value={ipsText}
        onChange={(e) => setIpsText(e.target.value)}
        disabled={loading || saving}
        placeholder={"192.168.1.1\n10.0.0.5"}
        style={{ height: "180px", minHeight: "180px", width: "100%" }}
      />
      <BeatLoader
        color="#36d7b7"
        loading={loading || saving}
        cssOverride={{
          textAlign: "center",
          marginTop: "16px",
        }}
      />
    </Modal>
  );
});

IpWhitelistModal.displayName = "IpWhitelistModal";

export default IpWhitelistModal;
